/**
 * Post-login redirect target resolution — storage-free and deliberately minimal.
 *
 * Sign-in is a popup, so the browser keeps the whole URL (path + query + hash) through
 * authentication. There's nothing to store or re-apply — we just keep the current page.
 */
/**
 * True only for same-origin, app-relative paths. Rejects absolute URLs and
 * protocol-relative (`//evil.com`, `/\evil`) targets to prevent open redirects
 * (OWASP A01 — Unvalidated Redirects & Forwards).
 */
export function isSafeInternalPath(path) {
    if (!path || !path.startsWith('/'))
        return false;
    if (path.startsWith('//') || path.startsWith('/\\'))
        return false;
    try {
        return new URL(path, window.location.origin).origin === window.location.origin;
    }
    catch {
        return false;
    }
}
/** Paths that aren't real destinations — the login/logout screens or the app's own base. */
function isIgnorable(pathname, appDefault) {
    if (/^\/(login|logout)(\/|$)/.test(pathname))
        return true;
    return pathname === appDefault || pathname === `${appDefault}/`;
}
/**
 * Where to send the user after login. The browser preserved the full URL (path + query
 * + hash) through the popup sign-in, so we simply keep the current page as-is — nothing
 * to store or re-apply. Falls back to the app default only when the current page is
 * `/login` or the bare base. An explicit `?redirect=` (validated) overrides, for links
 * that intentionally route through /login.
 */
export function resolvePostLoginTarget(appDefault) {
    const redirect = new URLSearchParams(window.location.search).get('redirect');
    if (isSafeInternalPath(redirect))
        return redirect;
    if (isIgnorable(window.location.pathname, appDefault))
        return appDefault;
    return window.location.pathname + window.location.search + window.location.hash;
}
